/**
 * VIB-642 Phase 6.4: cron heartbeats hook.
 *
 * Leser `public.cron_run_log` og aggregerer per jobname slik at admin/
 * observability-dashboardet kan vise sist run + status + runs siste 24h
 * for hver forventet PO-sync.
 *
 * Vi sammenligner mot en hardkodet liste over EXPECTED_JOBS (samme liste
 * som cron-heartbeat-monitor edge function), slik at jobber som ikke har
 * fyrt på lenge fortsatt vises som rad i tabellen (med status "stale" eller
 * "missing") — ikke filtrert bort fordi loggen er tom.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/** Status for en cron-job i UI-tabellen. */
export type CronJobStatus = "ok" | "error" | "stale" | "missing";

/** Forventet job. Holdes i sync med cron-heartbeat-monitor edge function. */
export interface ExpectedCronJob {
  /** Job-navn lagret i cron_run_log.jobname */
  jobname: string;
  /** Mennesklig label for UI */
  label: string;
  /** Maks alder før jobben regnes som "stale" (minutter). */
  maxAgeMin: number;
}

/** Aggregert rad for én cron-job, klar for UI-tabell. */
export interface CronHeartbeat {
  jobname: string;
  label: string;
  lastRun: string | null;
  lastStatus: CronJobStatus;
  durationMs: number | null;
  runsLast24h: number;
  errorsLast24h: number;
  maxAgeMin: number;
}

/** Rå rad fra cron_run_log. */
interface CronRunLogRow {
  jobname: string;
  ran_at: string;
  duration_ms: number | null;
  status: string;
}

/**
 * Liste over PO-syncer + heartbeat-monitor som dashboardet forventer å se
 * heartbeats fra. Speiler EXPECTED_JOBS i supabase/functions/cron-heartbeat-
 * monitor/index.ts. Hvis du legger til en ny cron-job må du oppdatere begge.
 */
export const EXPECTED_JOBS: ExpectedCronJob[] = [
  { jobname: "po-sync-vouchers", label: "PowerOffice — bilag", maxAgeMin: 360 },
  { jobname: "po-sync-invoices", label: "PowerOffice — fakturaer", maxAgeMin: 360 },
  {
    jobname: "po-sync-voucher-approvals",
    label: "PowerOffice — bilagsgodkjenninger",
    maxAgeMin: 360,
  },
  { jobname: "po-sync-financials", label: "PowerOffice — finansial", maxAgeMin: 30 },
];

function deriveStatus(
  lastRun: string | null,
  rawStatus: string | null,
  maxAgeMin: number,
  now: number,
): CronJobStatus {
  if (!lastRun) return "missing";
  if (rawStatus === "error") return "error";
  const ageMs = now - new Date(lastRun).getTime();
  if (ageMs > maxAgeMin * 60_000) return "stale";
  return "ok";
}

/**
 * Henter siste run-info + 24h aggregater for hver forventet cron-job.
 *
 * Strategien er enkel: hent ALLE rader siste 24h (avgrenset til de forventede
 * jobnames) + slå opp siste rad per job hvis ingen i siste 24h. For Sølvrev
 * sin volum (et titalls runs/dag per job × 4 jobs ≈ ~100 rader) er det greit
 * å gjøre i klienten uten egen DB-funksjon.
 */
export function useCronHeartbeats() {
  return useQuery({
    queryKey: ["cron-heartbeats"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CronHeartbeat[]> => {
      const expectedNames = EXPECTED_JOBS.map((j) => j.jobname);
      const cutoffIso = new Date(Date.now() - 24 * 60 * 60_000).toISOString();

      // Hent siste 24h for å regne runs/errors.
      const { data: last24hData, error: err24h } = await supabase
        .from("cron_run_log")
        .select("jobname, ran_at, duration_ms, status")
        .in("jobname", expectedNames)
        .gte("ran_at", cutoffIso)
        .order("ran_at", { ascending: false });
      if (err24h) throw err24h;
      const last24h = (last24hData ?? []) as CronRunLogRow[];

      // Slå opp siste rad per job (helt uten 24h-cutoff) for jobs som ikke
      // har fyrt siste døgn — vi vil fortsatt vise "sist sett: 3 dager siden".
      const { data: latestData, error: errLatest } = await supabase
        .from("cron_run_log")
        .select("jobname, ran_at, duration_ms, status")
        .in("jobname", expectedNames)
        .order("ran_at", { ascending: false })
        .limit(expectedNames.length * 5);
      if (errLatest) throw errLatest;
      const latestRows = (latestData ?? []) as CronRunLogRow[];

      const latestByJob = new Map<string, CronRunLogRow>();
      for (const row of latestRows) {
        if (!latestByJob.has(row.jobname)) {
          latestByJob.set(row.jobname, row);
        }
      }

      const now = Date.now();
      return EXPECTED_JOBS.map((job) => {
        const last = latestByJob.get(job.jobname) ?? null;
        const recent = last24h.filter((r) => r.jobname === job.jobname);
        const errorsLast24h = recent.filter((r) => r.status === "error").length;
        return {
          jobname: job.jobname,
          label: job.label,
          lastRun: last?.ran_at ?? null,
          lastStatus: deriveStatus(
            last?.ran_at ?? null,
            last?.status ?? null,
            job.maxAgeMin,
            now,
          ),
          durationMs: last?.duration_ms ?? null,
          runsLast24h: recent.length,
          errorsLast24h,
          maxAgeMin: job.maxAgeMin,
        };
      });
    },
  });
}
